// src/utils/billingHelpers.js
// ---------------------------------------------------------------------------
// Pure helpers + a small hook for the Billing page's customer-facing logic.
//
// Kept free of Chakra/react-router imports so it can be unit-tested directly
// in the CRA/jest environment (jest 27 cannot resolve react-router-dom v7 or
// Chakra-UI deep subpath imports — the pre-existing App.test.js fails the
// same way). Billing.js renders with these; tests cover them.
//
// This module touches ONLY presentation/notification logic — never billing
// persistence, webhook accounting, or Stripe data.
// ---------------------------------------------------------------------------
import { useEffect, useRef } from 'react';

// Stable notification ids — NotificationContext dedups on these.
export const BILLING_SUCCESS_TOAST_ID = 'billing-success-toast';
export const BILLING_CANCELLED_TOAST_ID = 'billing-cancelled-toast';

export function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Customer-friendly currency code for display only (e.g. "usd" -> "USD").
// Stripe reports lowercase ISO currency codes; the amount/identity are
// untouched — we only uppercase the code for the customer-facing label.
export function currencyLabel(currency) {
  return currency ? String(currency).toUpperCase() : '';
}

// Render a billing period's date range for display.
//
// The Stripe INITIAL invoice of a Checkout-created subscription can report a
// zero-length period (period_start === period_end) even though the customer
// purchased a full recurring period. That is invoice/payment data — we never
// rewrite the stored BillingPeriod — but we also must not show a confusing
// "Aug 21 – Aug 21" range. When the authoritative subscription item carries
// the true recurring period end (currentPeriodEnd) we use it for the display
// range; otherwise we fall back to a single truthful date. We never invent
// dates.
export function displayPeriodRange(period, subscription) {
  const start = period?.periodStart ? new Date(period.periodStart) : null;
  const end = period?.periodEnd ? new Date(period.periodEnd) : null;
  const fmt = (d) => formatDate(d.toISOString());

  if (start && end && start.getTime() === end.getTime()) {
    // Zero-length invoice period — prefer the matching subscription item's
    // real recurring period end when available.
    const realEnd = subscription?.items?.find(
      (item) =>
        item.subscriptionId === period.subscriptionId &&
        item.stripePriceId === period.stripePriceId &&
        item.currentPeriodEnd
    )?.currentPeriodEnd;
    if (realEnd && new Date(realEnd).getTime() > start.getTime()) {
      return `${fmt(start)} – ${fmt(new Date(realEnd))}`;
    }
    return fmt(start);
  }

  if (start && end) return `${fmt(start)} – ${fmt(end)}`;
  if (start) return fmt(start);
  return '—';
}

// Decide which checkout-return notification to show (or null for none).
// `billing=success` is NEVER trusted from the query parameter alone — it is
// only shown when the backend confirms an active subscription
// (subscribed === true). `billing=cancelled` is informational.
export function resolveBillingOutcomeNotification({ outcome, subscribed }) {
  if (outcome === 'success') {
    if (subscribed === true) {
      return {
        status: 'success',
        title: 'Subscription active',
        description: 'Your subscription is now active.',
        id: BILLING_SUCCESS_TOAST_ID,
      };
    }
    return null; // the query parameter is not proof of success
  }
  if (outcome === 'cancelled') {
    return {
      status: 'info',
      title: 'Checkout cancelled',
      description: 'You can subscribe again anytime.',
      id: BILLING_CANCELLED_TOAST_ID,
    };
  }
  return null;
}

// Return the query string with the handled `billing` param removed ('' when
// nothing remains), so a refresh/revisit cannot re-trigger the notification.
export function stripBillingParam(searchParams) {
  const params = new URLSearchParams(searchParams);
  params.delete('billing');
  return params.toString();
}

// Hook: handle the ?billing=checkout-return query parameter once the
// authoritative billing state has loaded.
//   * Waits for `loading === false`.
//   * Shows the success toast ONLY when the backend confirms an active
//     subscription (see resolveBillingOutcomeNotification).
//   * Uses a ref guard so it can never fire twice (React StrictMode-safe) and
//     a stable notification id so NotificationContext dedup also suppresses
//     duplicates.
//   * Strips the handled parameter from the URL via history replacement.
// `notify` is injected (NotificationContext pulls in Chakra, which the CRA
// jest environment cannot resolve).
export function useBillingOutcomeNotification({ loading, data, searchParams, notify }) {
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    if (loading) return; // wait for authoritative billing state to load
    const outcome = searchParams.get('billing');
    if (!outcome) return;

    // Mark handled first so a re-run can never fire the notification twice.
    handledRef.current = true;

    const notification = resolveBillingOutcomeNotification({
      outcome,
      subscribed: data?.subscribed === true,
    });
    if (notification) {
      if (notification.status === 'success') {
        notify.success(notification.title, notification.description, {
          id: notification.id,
        });
      } else {
        notify.info(notification.title, notification.description, {
          id: notification.id,
        });
      }
    }

    // Remove the handled query parameter so refresh/revisit can't re-trigger.
    const qs = stripBillingParam(searchParams);
    window.history.replaceState(
      null,
      '',
      qs ? `${window.location.pathname}?${qs}` : window.location.pathname
    );
  }, [loading, data, searchParams, notify]);
}

// ---------------------------------------------------------------------------
// Early Access offer.
//
// The Early Access package replaces the normal Starter offer for NEW /
// unsubscribed users:
//   * $20/month, "EARLY ACCESS PRICING" badge,
//   * $100 crossed out as the reference/original price,
//   * "This rate is available for 6 months only." / "After that, pricing will
//     increase." / "Subscribe now to get started.",
//   * checkout planKey = 'early_access'.
//
// The normal `starter` BillingPlan / $100 Stripe Price stays in the backend
// (existing subscriptions + historical billing may use it) but is NOT offered
// as a new checkout option.
//
// No automatic expiry: the offer is shown until it is manually removed after
// the global 6-month campaign ends.
// ---------------------------------------------------------------------------
export const EARLY_ACCESS_PLAN_KEY = 'early_access';

export const EARLY_ACCESS_OFFER = {
  planKey: EARLY_ACCESS_PLAN_KEY,
  name: 'Early Access',
  priceLabel: '$20 / month',
  // Full label kept for back-compat; the offer card renders the short
  // `originalPrice` (e.g. "$100") so the crossed-out price sits next to the
  // big discounted price without repeating "/ month".
  originalPriceLabel: '$100 / month',
  originalPrice: '$100',
  badge: 'EARLY ACCESS PRICING',
  rateNote: 'This rate is available for 6 months only.',
  increaseNote: 'After that, pricing will increase.',
  cta: 'Subscribe now to get started.',
};
